import * as React from "react"
import { batch } from "@preact/signals-react"
import { Alert, Box, CircularProgress, Paper, TablePagination, Typography } from "@mui/material"
import { IconSeeker } from "@ui-seeker/core"

import { LabelCountry } from "@components"
import {
    activityWindow,
    currentPage,
    groups,
    hasSearched,
    isLoadingList,
    loadError,
    pageSize,
    totalCount
} from "../@signals"
import DuplicateGroup from "./DuplicateGroup"


// Groups arrive ordered by site: split them into one section per site
function groupBySite(list) {
    const sections = []
    for (const group of list) {
        const last = sections[sections.length - 1]
        if (last && last.id_site === group.id_site) {
            last.groups.push(group)
        } else {
            sections.push({ id_site: group.id_site, site: group.site, countryCode: group.countryCode, groups: [group] })
        }
    }
    return sections
}


// States which products are counted, so the totals are never read as the whole mapping history
function ActivityNotice() {
    const activity = activityWindow.value
    if (!activity) return null

    if (activity.includeInactive) {
        return <Alert severity="warning" icon={<IconSeeker icon="clock-rotate-left" />} sx={{ mb: 2 }}>
            <strong>Including inactive products.</strong> The whole mapping history counts, so most of these
            duplicates are retired product_ids rather than mapping errors. Products not seen since{' '}
            <strong>{activity.activeSince}</strong> are tagged <strong>Inactive</strong>.
        </Alert>
    }

    return <Alert severity="info" icon={<IconSeeker icon="clock" />} sx={{ mb: 2 }}>
        <strong>Only active products:</strong> seen since <strong>{activity.activeSince}</strong> (last 30 days).
        A SKU shows up when two or more of its products on the same site are active. Tick
        "Include inactive products" in the filters to see the whole history.
    </Alert>
}


function EmptyState({ icon, text }) {
    return <Paper variant="outlined" sx={{ textAlign: 'center', py: 6, color: '#9ca3af' }}>
        <IconSeeker icon={icon} sx={{ fontSize: 48, mb: 1 }} />
        <Typography>{text}</Typography>
    </Paper>
}


export default function SkuDuplicatesList() {
    if (isLoadingList.value) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
        </Box>
    }

    if (loadError.value) {
        return <Alert severity="error">Error loading duplicates. Try the search again.</Alert>
    }

    if (!hasSearched.value) {
        return <EmptyState icon="filter" text="Choose a market or a site and search" />
    }

    if (groups.value.length === 0) {
        return <Box>
            <ActivityNotice />
            <EmptyState icon="circle-check" text="No SKU is mapped to more than one product on these sites" />
        </Box>
    }

    return <Box>
        <ActivityNotice />

        {groupBySite(groups.value).map(section => (
            <Box key={`${section.id_site}-${section.groups[0].sku.id_sku}`} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <LabelCountry country={section.countryCode} label={section.site} />
                    <Typography variant="caption" color="text.secondary">
                        {section.groups.length} duplicated SKUs on this page
                    </Typography>
                </Box>
                {section.groups.map(group => (
                    <DuplicateGroup key={`${group.id_site}-${group.sku.id_sku}`} group={group} />
                ))}
            </Box>
        ))}

        <TablePagination
            component="div"
            count={totalCount.value}
            page={currentPage.value}
            onPageChange={(_, page) => currentPage.value = page}
            rowsPerPage={pageSize.value}
            onRowsPerPageChange={e => batch(() => {
                pageSize.value = parseInt(e.target.value, 10)
                currentPage.value = 0
            })}
            rowsPerPageOptions={[20, 50, 100]}
            labelRowsPerPage="SKUs per page"
        />
    </Box>
}
